import type { AccessQueueItemPublic } from '@accounts/shared';
import { useState } from 'react';

import { STR } from '../../../../i18n';
import { relativeTime } from '../../../../lib/format';
import { Modal } from '../../../../ui/Modal';
import { Callout, EmptyRow } from '../../../../ui/Primitives';
import { checkboxStyle, modalButtonStyle, monoStyle } from '../../../../ui/styles';

import { type PickItem, pickerSystems, toneTotals } from './picker';
import { toneBar } from './tone';
import { ToneCounts, ToneLegend, ToneWord } from './ToneParts';

/**
 * NEW EMAIL TO IT, STEP 1 (FINAL demo, change 1): pick the open outside-system work that goes
 * in ONE email. Grouped by system, then person; a system's box takes all of it. Each line says
 * Set up or Remove in its tone. `items` is the work that is not with IT yet — the caller
 * leaves out anything already in a sent email. `initial` carries the Queue's ticks (or a
 * lane's "Draft email to IT for N"), so the picker opens with them already picked.
 */
export function ItPickDialog({
  items,
  initial,
  pending,
  error,
  onCancel,
  onDraft,
}: {
  items: readonly AccessQueueItemPublic[];
  initial: ReadonlySet<string>;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onDraft: (picked: readonly AccessQueueItemPublic[]) => void;
}) {
  const systems = pickerSystems(items);
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set(initial));

  const all: PickItem[] = systems.flatMap((system) => system.items);
  const chosen = all.filter((entry) => picked.has(entry.item.id));
  const totals = toneTotals(chosen);

  function set(ids: readonly string[], on: boolean) {
    setPicked((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  const draftLabel =
    chosen.length === 0
      ? STR.itEmails.draftEmail
      : totals.setUp > 0 && totals.remove > 0
        ? STR.itEmails.draftWithSplit(chosen.length, totals.setUp, totals.remove)
        : STR.itEmails.draftWith(chosen.length);

  return (
    <Modal
      title={STR.itEmails.newEmail}
      lead={STR.itEmails.pickLead}
      width="min(760px,100%)"
      dismissable={!pending}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            className="ghost"
            style={modalButtonStyle}
            disabled={pending}
            onClick={onCancel}
          >
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={pending || chosen.length === 0}
            onClick={() => {
              onDraft(chosen.map((entry) => entry.item));
            }}
          >
            {pending ? STR.itEmails.drafting : draftLabel}
          </button>
        </>
      }
    >
      <div style={{ padding: '16px 22px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="tx-subtle" style={{ fontSize: 12.5 }}>
          {STR.itEmails.pickHelp}
        </div>
        <ToneLegend />
        <div
          style={{ border: '1px solid var(--border)', borderRadius: 10, maxHeight: 380, overflow: 'auto' }}
        >
          {systems.length === 0 ? (
            <EmptyRow>{STR.itEmails.pickEmpty}</EmptyRow>
          ) : (
            systems.map((system) => {
              const ids = system.items.map((entry) => entry.item.id);
              const allOn = ids.every((id) => picked.has(id));
              return (
                <div key={system.id}>
                  <label
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: 'var(--surface-2)',
                      borderBottom: '1px solid var(--border)',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      style={{ ...checkboxStyle, margin: 0 }}
                      checked={allOn}
                      onChange={(event) => {
                        set(ids, event.target.checked);
                      }}
                      aria-label={STR.itEmails.pickSystem(system.label)}
                    />
                    <b style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 650 }}>{system.label}</b>
                    <ToneCounts setUp={system.setUp} remove={system.remove} />
                  </label>
                  {system.items.map(({ item, tone }) => (
                    <label
                      key={item.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '18px 58px minmax(0,1fr) auto',
                        gap: 10,
                        alignItems: 'center',
                        padding: '7px 12px 7px 22px',
                        borderBottom: '1px solid var(--border)',
                        fontSize: 12.5,
                        cursor: 'pointer',
                        boxShadow: toneBar(tone),
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{ ...checkboxStyle, margin: 0 }}
                        checked={picked.has(item.id)}
                        onChange={(event) => {
                          set([item.id], event.target.checked);
                        }}
                        aria-label={STR.itEmails.pickOne(
                          tone === 'setup' ? STR.itEmails.setUp : STR.itEmails.remove,
                          item.user.name,
                        )}
                      />
                      <ToneWord tone={tone} />
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <b style={{ fontWeight: 600 }}>{item.user.name}</b>{' '}
                        <span className="tx-subtle" style={{ ...monoStyle, fontSize: 12 }}>
                          {item.login ?? STR.itEmails.noLoginYet}
                        </span>
                      </span>
                      <span
                        className={item.overdue ? 'tx-danger-strong' : 'tx-subtle'}
                        style={{
                          fontSize: 11.5,
                          fontWeight: item.overdue ? 700 : 400,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {item.overdue
                          ? STR.access.queue.overdueAge(relativeTime(item.since))
                          : relativeTime(item.since)}
                      </span>
                    </label>
                  ))}
                </div>
              );
            })
          )}
        </div>
        <div style={{ fontSize: 12.5 }}>
          {chosen.length === 0 ? (
            <span className="tx-subtle">{STR.access.request.nothingPicked}</span>
          ) : (
            <>
              <b>{STR.itEmails.pickedLine(chosen.length)}</b>{' '}
              <ToneCounts setUp={totals.setUp} remove={totals.remove} />
            </>
          )}
        </div>
        {error === null ? null : (
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        )}
      </div>
    </Modal>
  );
}
